import { useEffect, useMemo, useRef, useState } from 'react'
import { Mail, Phone, User, Users, X } from 'lucide-react'
import { useDirectory } from '@/contexts/directory.context'
import { type RoleWithMembers, type Staff } from '@/lib/api/directory.client'
import {
  parseTargets,
  type ParsedTarget,
  type TargetChannel,
} from '@/lib/handoff-mentions'
import {
  phoneFormatter,
  splitPhoneExtension,
  validatePhoneE164,
} from '@/lib/validators'

interface MentionMultiSelectProps {
  value: string
  onChange: (next: string) => void
  channel: TargetChannel
  // A dial order: the same person or role may be picked more than once.
  allowRepeats?: boolean
  placeholder?: string
  disabled?: boolean
  hasError?: boolean
}

type Suggestion = {
  kind: 'role' | 'staff'
  id: string
  name: string
  sublabel: string
}

// Builds the @-mention dropdown. Hides a role or staff member that has no address
// on this channel, or whose exact chip is already picked unless `allowRepeats`
// (a dial order, where someone can be tried again) — overlap is allowed.
export function buildMentionSuggestions({
  value,
  channel,
  roles,
  staff,
  query: rawQuery,
  allowRepeats = false,
}: {
  value: string
  channel: TargetChannel
  allowRepeats?: boolean
  roles: RoleWithMembers[]
  staff: Staff[]
  query: string
}): { suggestions: Suggestion[]; totalMatches: number } {
  const query = rawQuery.toLowerCase()
  const matches: Suggestion[] = []

  const pickedTargets = allowRepeats
    ? []
    : parseTargets(value, channel, roles, staff)
  const pickedStaffIds = new Set(
    pickedTargets.flatMap(target =>
      target.kind === 'staff' ? [target.staffId] : [],
    ),
  )
  const pickedRoleIds = new Set(
    pickedTargets.flatMap(target =>
      target.kind === 'role' ? [target.roleId] : [],
    ),
  )

  for (const role of roles) {
    if (query && !role.name.toLowerCase().includes(query)) continue
    if (pickedRoleIds.has(role.id)) continue
    const reachable = role.members.filter(member =>
      channel === 'phone' ? !!member.staff.phone : !!member.staff.email,
    )
    // A role with no one reachable on this channel can never contribute.
    if (reachable.length === 0) continue
    matches.push({
      kind: 'role',
      id: role.id,
      name: role.name,
      sublabel: `Role · ${reachable.length} member${reachable.length === 1 ? '' : 's'}`,
    })
  }

  for (const staffMember of staff) {
    if (channel === 'phone' && !staffMember.phone) continue
    if (channel === 'email' && !staffMember.email) continue
    const matchName = staffMember.name.toLowerCase().includes(query)
    const matchEmail = (staffMember.email ?? '').toLowerCase().includes(query)
    const matchPhone = (staffMember.phone ?? '').toLowerCase().includes(query)
    if (query && !(matchName || matchEmail || matchPhone)) continue
    if (pickedStaffIds.has(staffMember.id)) continue
    matches.push({
      kind: 'staff',
      id: staffMember.id,
      name: staffMember.name,
      sublabel:
        [staffMember.phone, staffMember.email].filter(Boolean).join(' · ') ||
        'Staff',
    })
  }

  matches.sort((left, right) => left.name.localeCompare(right.name))
  return { suggestions: matches.slice(0, 8), totalMatches: matches.length }
}

export function MentionMultiSelect({
  value,
  onChange,
  channel,
  allowRepeats,
  placeholder,
  disabled,
  hasError,
}: MentionMultiSelectProps) {
  const { roles, staff } = useDirectory()
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const pills = useMemo(
    () =>
      value
        .split(',')
        .map(piece => piece.trim())
        .filter(Boolean),
    [value],
  )
  const targets = useMemo(
    () => parseTargets(value, channel, roles, staff),
    [value, channel, roles, staff],
  )

  const { suggestions, totalMatches } = useMemo(
    () =>
      buildMentionSuggestions({
        value,
        channel,
        roles,
        staff,
        query: mentionQuery,
        allowRepeats,
      }),
    [roles, staff, mentionQuery, channel, value, allowRepeats],
  )

  useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0)
  }, [suggestions, highlight])

  const commitPill = (raw: string): boolean => {
    const trimmed = raw.trim().replace(/,+$/, '').trim()
    if (!trimmed) return false

    let toCommit = trimmed
    const looksLikeMention = trimmed.startsWith('@')
    if (channel === 'phone' && !looksLikeMention) {
      const result = validatePhoneE164(trimmed)
      if (!result) {
        setError(`"${trimmed}" is not a valid phone number`)
        return false
      }
      toCommit = result.extension
        ? `${result.value}#${result.extension}`
        : result.value
    }

    setError(null)
    onChange([...pills, toCommit].join(', '))
    setInputValue('')
    setMentionOpen(false)
    setMentionQuery('')
    return true
  }

  const removePillAt = (indexToRemove: number) => {
    const next = pills.filter((_, index) => index !== indexToRemove)
    onChange(next.join(', '))
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    if (error) setError(null)

    // Comma → commit everything before it as a pill
    if (next.includes(',')) {
      const [head, ...rest] = next.split(',')
      const committed = commitPill(head)
      const remainder = rest.join(',').trim()
      if (!committed) {
        // Keep what they typed (including the rest) so they can correct it
        setInputValue([head, ...rest].join(',').trim())
      } else if (remainder) {
        setInputValue(remainder)
      }
      return
    }

    setInputValue(next)

    // @-trigger detection (same as MentionInput)
    const cursor = event.target.selectionStart ?? next.length
    const before = next.slice(0, cursor)
    const atIndex = before.lastIndexOf('@')
    if (atIndex < 0) {
      setMentionOpen(false)
      return
    }
    const fragment = before.slice(atIndex + 1)
    if (/\s/.test(fragment)) {
      setMentionOpen(false)
      return
    }
    const charBefore = atIndex > 0 ? before[atIndex - 1] : ''
    if (charBefore && /[A-Za-z0-9]/.test(charBefore)) {
      setMentionOpen(false)
      return
    }
    setMentionQuery(fragment)
    setMentionOpen(true)
    setHighlight(0)
  }

  const pickSuggestion = (suggestion: Suggestion) => {
    commitPill(`@${suggestion.name}`)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (mentionOpen && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlight(current => (current + 1) % suggestions.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlight(
          current => (current - 1 + suggestions.length) % suggestions.length,
        )
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        pickSuggestion(suggestions[highlight])
        return
      }
      if (event.key === 'Escape') {
        setMentionOpen(false)
        return
      }
    }

    if (event.key === 'Enter' || (event.key === ',' && inputValue.trim())) {
      event.preventDefault()
      commitPill(inputValue)
      return
    }

    if (event.key === 'Backspace' && inputValue === '' && pills.length > 0) {
      event.preventDefault()
      removePillAt(pills.length - 1)
    }
  }

  const handleBlur = () => {
    setTimeout(() => setMentionOpen(false), 100)
    if (inputValue.trim()) commitPill(inputValue)
  }

  return (
    <div className="relative">
      <div
        className={`
          flex flex-wrap items-center gap-1.5 min-h-9 px-2 py-1
          rounded-md border bg-background text-sm
          focus-within:ring-2 focus-within:ring-offset-2
          ${error || hasError ? 'border-destructive focus-within:ring-destructive' : 'border-input focus-within:ring-ring'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={() => inputRef.current?.focus()}
      >
        {pills.map((pill, index) => (
          <Pill
            key={`${pill}-${index}`}
            target={targets[index]}
            onRemove={() => removePillAt(index)}
            disabled={disabled}
            roles={roles}
            channel={channel}
          />
        ))}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={pills.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[8rem] bg-transparent outline-none placeholder:text-muted-foreground"
          spellCheck={false}
        />
      </div>

      {error && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {mentionOpen && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 top-full mt-1 w-72 max-h-64 overflow-y-auto bg-popover border rounded-md shadow-lg">
          {suggestions.map((suggestion, index) => (
            <button
              type="button"
              key={`${suggestion.kind}:${suggestion.id}`}
              onMouseEnter={() => setHighlight(index)}
              onMouseDown={event => {
                event.preventDefault()
                pickSuggestion(suggestion)
              }}
              className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${
                index === highlight ? 'bg-accent' : ''
              }`}
            >
              {suggestion.kind === 'role' ? (
                <Users className="h-4 w-4 text-muted-foreground" />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{suggestion.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {suggestion.sublabel}
                </div>
              </div>
            </button>
          ))}
          {totalMatches > suggestions.length && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/50 border-t">
              {totalMatches - suggestions.length} more — keep typing to filter
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface PillProps {
  target: ParsedTarget | undefined
  onRemove: () => void
  disabled?: boolean
  roles: RoleWithMembers[]
  channel: TargetChannel
}

function Pill({ target, onRemove, disabled, roles, channel }: PillProps) {
  if (!target) return null

  const base = describePill(target, roles, channel)

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${base.tone}`}
      title={base.title}
    >
      {base.icon}
      <span>{base.label}</span>
      {!disabled && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-0.5 hover:bg-foreground/10 rounded p-0.5"
          aria-label={`Remove ${base.label}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

export function describePill(
  target: ParsedTarget,
  roles: RoleWithMembers[],
  channel: TargetChannel,
): {
  icon: React.ReactNode
  label: string
  tone: string
  title?: string
} {
  switch (target.kind) {
    case 'role': {
      const role = roles.find(({ id }) => id === target.roleId)
      const lines =
        role?.members.map(member => {
          const address =
            channel === 'email' ? member.staff.email : member.staff.phone
          return address
            ? `${member.staff.name}: ${address}`
            : member.staff.name
        }) ?? []
      // Lead with how the role dispatches: every member, or just whoever the
      // on-call calendar picks for the day. A rotation with no fallback can't
      // reach anyone on unassigned days — call that out.
      const isRotation = role?.schedulingMode === 'ROTATION'
      const header = isRotation
        ? "Reaches only the day's on-call member:"
        : 'Reaches all members:'
      const noFallbackWarning =
        isRotation && role && !role.rotationFallbackStaffId
          ? "\nThis on-call rotation has no fallback — it won't reach anyone on days that are not explicitly assigned."
          : ''
      const title =
        lines.length > 0
          ? [header, ...lines].join('\n') + noFallbackWarning
          : 'No members'
      return {
        icon: <Users className="h-3 w-3" />,
        label: target.raw,
        tone: 'bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-100',
        title,
      }
    }
    case 'staff':
      return {
        icon: <User className="h-3 w-3" />,
        label: target.raw,
        tone: 'bg-purple-50 text-purple-900 dark:bg-purple-950 dark:text-purple-100',
      }
    case 'literal-phone': {
      const { phone, extension } = splitPhoneExtension(target.value)
      const formatted = phoneFormatter.format(phone)
      const label = extension ? `${formatted} #${extension}` : formatted
      return {
        icon: <Phone className="h-3 w-3" />,
        label,
        tone: 'bg-secondary text-secondary-foreground',
        title: target.value,
      }
    }
    case 'literal-email':
      return {
        icon: <Mail className="h-3 w-3" />,
        label: target.raw,
        tone: 'bg-secondary text-secondary-foreground',
      }
    case 'unknown':
      return {
        icon: <X className="h-3 w-3" />,
        label: target.raw,
        tone: 'bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
        title: target.reason,
      }
  }
}
